import React from "react";
import { css } from "@emotion/core";
import { motion, useAnimation } from "framer-motion";
import { useMount } from "react-use";
import { Desktop, Tablet } from "../../constants/responsive";

const Circle = ({ size, top, left, color }) => {
  const controls = useAnimation();
  useMount(() => {
    const startBig = Math.random() > 0.5;
    const min = 0.8 - 0.3 * Math.random();
    const max = 1.2 + 0.3 * Math.random();
    controls
      .start({
        opacity: startBig ? max / max : min / max,
        scale: startBig ? max : min,
        transition: {
          delay: 1 + Math.random() * 2,
          type: "spring",
          mass: 1,
          damping: 10,
          stiffness: 100,
        },
      })
      .then(() => {
        controls.start({
          opacity: startBig ? min / max : max / max,
          scale: startBig ? min : max,
          transition: {
            repeat: Infinity,
            repeatType: "mirror",
            duration: 3 + Math.random() * 2,
          },
        });
      });
  });
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={controls}
      css={css`
        width: ${size}%;
        height: ${size}%;
        border-radius: 100%;
        position: absolute;
        top: ${top}%;
        left: ${left}%;
        opacity: 0;
        background: ${color};
      `}
    />
  );
};

const InstitutionsAnimation = () => {
  return (
    <div
      css={css`
        position: absolute;
        top: -55px;
        width: 280px;
        height: 280px;
        // transform: translate(64%,-14px);
        left: 160px;
        @media (min-width: 580px) {
          top: 15px;
          left: auto;
          right: 23px;
        }
        ${Tablet} {
          top: -35px;
          left: 50%;
          right: auto;
          width: 300px;
          height: 300px;
          transform: translate(64px, 65px);
        }
        ${Desktop} {
          width: 475px;
          height: 475px;
          transform: translate(70px, 90px);
        }
      `}
    >
      {new Array(16).fill(0).map((_val, index) => {
        // Center Lines
        const length = 10;
        const thickness = 2;
        const size = 6;
        const gap = 14;
        const x = index % 4;
        const y = Math.floor(index / 4);
        const initialOffset = 25;
        const xOffset = gap * x + (y > x ? 5 : 2);
        const yOffset = gap * y + (x > y ? 5 : 2);
        const top = initialOffset + yOffset;
        const left = initialOffset + xOffset;
        const bgSize = size + gap * 3;
        const bgPercentX = (bgSize / (y > x ? length : thickness)) * 100;
        const bgPercentY = (bgSize / (x > y ? length : thickness)) * 100;
        const bgX = (100 * xOffset) / (y > x ? length : thickness);
        const bgY = (100 * yOffset) / (x > y ? length : thickness);

        if (x !== y) {
          const dampening = 1 + (Math.max(x, y) - 1) * 0.8;
          const speed = 0.6;
          return (
            <motion.div
              key={index}
              initial={{
                scaleX: y > x ? 0 : 1,
                scaleY: x > y ? 0 : 1,
                originX: y > x ? 0 : 0.5,
                originY: x > y ? 1 : 0.5,
              }}
              animate={{
                // opacity: 1,
                scaleX: 1,
                scaleY: 1,
                transition: {
                  // delay: 1 + 0.2 * (y > x ? y : x),
                  delay:
                    1.2 +
                    Math.max(x, y) * 0.2 +
                    (speed * (0.8 * (y > x ? x : 2 * x - y - 1))) / dampening,
                  duration: speed / dampening,
                  // type: "spring",
                  // mass: 1,
                  // damping: 10,
                  // stiffness: 100,
                },
              }}
              css={css`
                width: ${y > x ? length : thickness}%;
                height: ${x > y ? length : thickness}%;
                position: absolute;
                top: ${top}%;
                left: ${left}%;
                /* opacity: 0; */
                overflow: hidden;
              `}
            >
              <div
                css={css`
                  background-image: linear-gradient(45deg, #ff80ab, #ee2f79);
                  position: absolute;
                  top: -${bgY}%;
                  left: -${bgX}%;
                  width: ${bgPercentX}%;
                  height: ${bgPercentY}%;
                `}
              />
            </motion.div>
          );
        } else {
          return null;
        }
      })}

      {new Array(16).fill(0).map((_val, index) => {
        // Center Circles
        const size = 6;
        const gap = 14;
        const x = index % 4;
        const y = Math.floor(index / 4);
        const initialOffset = 25;
        const xOffset = gap * x;
        const yOffset = gap * y;
        const top = initialOffset + yOffset;
        const left = initialOffset + xOffset;
        const bgSize = size + gap * 3;
        const bgPercent = (bgSize / size) * 100;
        const bgX = (100 * xOffset) / size;
        const bgY = (100 * yOffset) / size;
        return (
          <motion.div
            key={`center-circle-${index}`}
            initial={{ scale: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                delay: 1 + 0.2 * (x + y),
                type: "spring",
                mass: 1,
                damping: 10,
                stiffness: 100,
              },
            }}
            css={css`
              width: ${size}%;
              height: ${size}%;
              border-radius: 100%;
              position: absolute;
              top: ${top}%;
              left: ${left}%;
              opacity: 0;
              overflow: hidden;
              will-change: transform;
              z-index: 0;
            `}
          >
            <div
              css={css`
                background: linear-gradient(45deg, #ff80ab, #ee2f79);
                background: -webkit-linear-gradient(45deg, #ff80ab, #ee2f79);
                position: absolute;
                top: -${bgY}%;
                left: -${bgX}%;
                width: ${bgPercent}%;
                height: ${bgPercent}%;
              `}
            />
          </motion.div>
        );
      })}
      <Circle top={0} left={26} size={5} color="#f13d82" />
      <Circle top={10} left={40} size={4} color="#f0377e" />
      <Circle top={14} left={67} size={5} color="#1ea4dd" />
      <Circle top={27} left={12} size={6} color="#c3e7f9" />
      <Circle top={28} left={97} size={3} color="#1ca3dd" />
      <Circle top={41} left={91} size={4} color="#ee2f79" />
      <Circle top={55} left={80} size={4} color="#ff80ab" />
      <Circle top={55} left={0} size={4} color="#ef337c" />
      <Circle top={81} left={40} size={5} color="#c3e7f9" />
      <Circle top={81} left={80} size={4} color="#99d6f2" />
      <Circle top={92} left={54} size={4} color="#fb6fa0" />
    </div>
  );
};

export default InstitutionsAnimation;
